import { useEffect, useState } from "react";
import { Link, Route, Switch, useLocation, useSearch } from "wouter";

import { apiClient } from "./api/client.js";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { DeviceEvents } from "./pages/DeviceEvents.js";
import { FreePlayTimes } from "./pages/FreePlayTimes.js";
import { GameSettings } from "./pages/GameSettings.js";
import { Home } from "./pages/Home.js";
import { Login } from "./pages/Login.js";
import { ScheduledDowntime } from "./pages/ScheduledDowntime.js";
import { Sessions } from "./pages/Sessions.js";
import { SetupDevice } from "./pages/SetupDevice.js";
import { Transactions } from "./pages/Transactions.js";

const NAV_ITEMS = [
  { href: "/", label: "Dashboard" },
  { href: "/sessions", label: "Sessions" },
  { href: "/transactions", label: "Transactions" },
  { href: "/free-play-times", label: "Free Play Times" },
  { href: "/scheduled-downtime", label: "Scheduled Downtime" },
  { href: "/game-settings", label: "Game Settings" },
] as const;

function isNavActive(location: string, href: string): boolean {
  if (href === "/") return location === "/";
  return location === href || location.startsWith(`${href}/`);
}

function navLinkClass(location: string, href: string, mobile = false): string {
  const active = isNavActive(location, href);
  const base = mobile
    ? "block px-4 py-3 text-base no-underline transition-colors border-b border-border"
    : "text-sm no-underline transition-colors";
  return active
    ? `${base} text-accent font-medium`
    : `${base} text-text-muted hover:text-text-primary`;
}

function VenueSelector({
  className = "",
  onChange,
}: {
  className?: string;
  onChange?: () => void;
}) {
  const { user, selectedVenue, setSelectedVenue } = useAuth();
  if (!user || user.venues.length <= 1) return null;

  return (
    <select
      value={selectedVenue?.id ?? ""}
      onChange={(e) => {
        const venue = user.venues.find((v) => v.id === e.target.value);
        if (venue) setSelectedVenue(venue);
        onChange?.();
      }}
      className={`text-sm px-2 py-1.5 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors ${className}`}
    >
      {user.venues.map((v) => (
        <option key={v.id} value={v.id}>
          {v.name}
        </option>
      ))}
    </select>
  );
}

function Header() {
  const { user, logout, selectedVenue } = useAuth();
  const [location] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header className="relative z-40 border-b border-border bg-surface">
      <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-3 md:gap-6">
          <a
            href="/"
            className="truncate text-lg font-bold text-text-primary no-underline hover:text-accent transition-colors"
          >
            TeamTable Venue
          </a>
          <VenueSelector className="hidden md:block max-w-[12rem]" />
        </div>

        <nav className="hidden md:flex items-center gap-4">
          {NAV_ITEMS.map((item) => (
            <Link key={item.href} href={item.href} className={navLinkClass(location, item.href)}>
              {item.label}
            </Link>
          ))}
        </nav>

        {user && (
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <span className="text-sm text-text-secondary">{user.display_name}</span>
            <button
              onClick={logout}
              className="text-sm text-text-muted hover:text-text-primary transition-colors"
            >
              Sign out
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-border text-text-primary hover:border-accent transition-colors"
          aria-expanded={menuOpen}
          aria-controls="venue-mobile-nav"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? (
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {menuOpen && (
        <>
          <button
            type="button"
            className="md:hidden fixed inset-0 top-[57px] z-40 bg-black/50"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          />
          <nav
            id="venue-mobile-nav"
            className="md:hidden absolute left-0 right-0 top-full z-50 max-h-[calc(100dvh-57px)] overflow-y-auto border-b border-border bg-surface-raised shadow-lg"
          >
            {user && (
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium text-text-primary truncate">
                  {user.display_name}
                </p>
                {selectedVenue && user.venues.length === 1 && (
                  <p className="text-xs text-text-muted mt-0.5 truncate">{selectedVenue.name}</p>
                )}
                <VenueSelector className="mt-2 w-full" onChange={() => setMenuOpen(false)} />
              </div>
            )}
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={navLinkClass(location, item.href, true)}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            {user && (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="w-full px-4 py-3 text-left text-base text-text-muted hover:text-text-primary transition-colors"
              >
                Sign out
              </button>
            )}
          </nav>
        </>
      )}
    </header>
  );
}

function VerifyPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { refresh } = useAuth();
  const [error, setError] = useState("");
  const token = new URLSearchParams(search).get("token");

  useEffect(() => {
    if (!token) {
      setError("No token provided");
      return;
    }

    apiClient["/api/v1/venue/auth/verify-link"]
      ["post"]({
        parameters: {},
        body: { token },
      })
      .then(async (res) => {
        if (res.code === 200) {
          await refresh();
          navigate("/");
        } else {
          setError("Invalid or expired token");
        }
      })
      .catch(() => setError("Verification failed"));
  }, [token, navigate, refresh]);

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-500">{error}</p>
          <a href="/" className="mt-4 inline-block text-sm text-accent hover:underline">
            Back to sign in
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center">
      <p className="text-text-secondary">Verifying...</p>
    </main>
  );
}

function AppContent() {
  const { user, loading, accessDenied, logout } = useAuth();
  const [location] = useLocation();
  const search = useSearch();

  if (location.startsWith("/auth/verify")) {
    return <VerifyPage />;
  }

  if (loading) {
    return null;
  }

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h1 className="text-xl font-bold text-text-primary">Access Denied</h1>
        <p className="text-text-secondary">You do not have venue operator privileges.</p>
        <button
          onClick={logout}
          className="px-4 py-2 rounded bg-accent text-white hover:bg-accent-hover transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (!user) {
    // Scanning the QR code on a new device is the one deep link an operator can
    // reach while signed out, and the setup code lives in its query string —
    // landing them on the dashboard instead would lose it.
    const returnTo = location.startsWith("/setup-device")
      ? location + (search ? `?${search}` : "")
      : undefined;
    return <Login returnTo={returnTo} />;
  }

  return (
    <>
      <Header />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/setup-device" component={SetupDevice} />
        <Route path="/sessions" component={Sessions} />
        <Route path="/devices/:id/events" component={DeviceEvents} />
        <Route path="/transactions" component={Transactions} />
        <Route path="/free-play-times" component={FreePlayTimes} />
        <Route path="/scheduled-downtime" component={ScheduledDowntime} />
        <Route path="/game-settings" component={GameSettings} />
      </Switch>
    </>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
